import React, { useState, useEffect } from 'react';
import styled from 'astroturf/react';
import useResizeObserver, {
  UseResizeObserverCallback,
} from '@react-hook/resize-observer';

import useScreenSize from 'js/utils/hooks/useScreenSize';
import ImpressionWatcher, {
  Props as ImpressionProps,
} from 'js/utils/impressions/ImpressionWatcher';

class ServerSideResizeObserverPolyfill {
  constructor() {}
  observe() {}
  unobserve() {}
}

function useResizeObserverSsr<T extends Element>(
  target: React.RefObject<T> | React.ForwardedRef<T> | T | null,
  callback: UseResizeObserverCallback,
) {
  return useResizeObserver(target, callback, {
    polyfill:
      typeof window === 'undefined' ? ServerSideResizeObserverPolyfill : null,
  });
}

const Container = styled.div`
  position: relative;
  overflow: hidden;
`;

const PrefixWithEllipsis = styled.span`
  display: block;

  &::before {
    content: '...';
    padding-right: 0.2em;
    padding-left: 1em;
    text-decoration: none;
    display: inline-block;
  }
`;

const SeeMoreContainer = styled(ImpressionWatcher)`
  position: absolute;
  bottom: 0;
  right: 0;
  display: flex;
  cursor: pointer;

  > {
    ::before {
      content: '';
      width: 1em;
      display: block;
      mask-image: linear-gradient(to right, rgba(0, 0, 0, 0), black);
      height: 100%;
      position: absolute;
      right: 100%;
      background: inherit;
    }
  }
`;

type Props = {
  height?: number;
  collapsible?: boolean;
  verb?: string;
  styledMoreElement?: React.ReactElement;
  readMoreImpressionProps?: ImpressionProps;
};

export const TruncateHeight: React.FC<React.PropsWithChildren<Props>> = ({
  height,
  collapsible = false,
  children,
  verb = 'see',
  styledMoreElement = <div style={{ background: 'white' }} />,
  readMoreImpressionProps = { source: 'read-more', active: false },
}) => {
  height = height || 20 * 6;
  const size = useScreenSize();
  const [canTruncate, setCanTruncate] = useState(false);
  const [truncate, setTruncate] = useState(true);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const showSeeMore = canTruncate && (collapsible || truncate);

  const calculateCanTruncate = (containerDiv: HTMLDivElement) => {
    setCanTruncate(containerDiv.scrollHeight !== containerDiv.offsetHeight);
  };

  useEffect(() => {
    if (container) {
      calculateCanTruncate(container);
    }
  }, [container, size]);

  useResizeObserverSsr(container, (entry) => {
    const target = entry.target as HTMLDivElement;
    calculateCanTruncate(target);
  });

  return (
    <Container
      style={{
        maxHeight: truncate && height !== undefined ? height : undefined,
      }}
      ref={setContainer}
    >
      {children}
      {showSeeMore && (
        <SeeMoreContainer
          onClick={() => setTruncate(!truncate)}
          {...readMoreImpressionProps}
        >
          {React.cloneElement(styledMoreElement, {
            children: (
              <PrefixWithEllipsis>
                {verb}
                &nbsp;
                {truncate ? 'more' : 'less'}
              </PrefixWithEllipsis>
            ),
          })}
        </SeeMoreContainer>
      )}
    </Container>
  );
};
